import React, {createContext, useLayoutEffect, useState, useEffect} from "react";
import {Route, Switch} from "react-router-dom";
import {Environment, Network, RecordSource, Store} from "relay-runtime";
import graphql from "babel-plugin-relay/macro";
import {RelayEnvironmentProvider, useLazyLoadQuery} from "react-relay";
import {useAuth0, withAuthenticationRequired} from "@auth0/auth0-react";
import LogRocket from "logrocket";
import setupLogRocketReact from "logrocket-react";
import DefaultLayout from "./containers/DefaultLayout";
import {fetchGraphQL} from "./utils/apiRequest";
import routes from "./routes";

import type {AppQuery} from "./__generated__/AppQuery.graphql";

import "./App.css";
// Styles
// CoreUI Icons Set
import "@coreui/icons/css/coreui-icons.min.css";
// Import Flag Icons Set
import "flag-icon-css/css/flag-icon.min.css";
// Import Font Awesome Icons Set
import "font-awesome/css/font-awesome.min.css";
// Import Simple Line Icons Set
import "simple-line-icons/css/simple-line-icons.css";
// Import Main styles for this application
import "./scss/style.css"
import {LOGROCKET_APP_ID} from "./utils/config";

LogRocket.init(LOGROCKET_APP_ID);
setupLogRocketReact(LogRocket);

export const AppContext = createContext();

const AppRoot = () => {
  const {getAccessTokenSilently} = useAuth0();
  const [relayEnvironment, setRelayEnvironment] = useState(null);

  useLayoutEffect(() => {
    (async () => {
      const accessToken = await getAccessTokenSilently();
      const network = Network.create((params, variables) => fetchGraphQL(accessToken, params.text, variables));
      const store = new Store(new RecordSource());
      const environment = new Environment({ network, store });
      setRelayEnvironment(environment);
    })();
  }, [getAccessTokenSilently])

  return (relayEnvironment &&
    <RelayEnvironmentProvider environment={relayEnvironment}>
      <React.Suspense fallback={"Loading Portal..."}>
        <App />
      </React.Suspense>
    </RelayEnvironmentProvider>
  );
}

const App = () => {
  const {user} = useAuth0();
  const {userProfile, userGroupsUserServiceInfo, dashboardApps,userAllPermissions} = useLazyLoadQuery(   // <AppQuery>
    graphql`
      query AppQuery($decodedEmail: String!, $encodedEmail: String!) {
        userProfile (userEmail: $decodedEmail) {
          id
          emailAddress
          status
          uuid
          firstName
          lastName
          title
          companyName
          auth0Id
        }
        userGroupsUserServiceInfo(email: $encodedEmail) {
          groupName
          groupUuid
          groupDetails{
            userGroupUuid
            services{
              countries{
                services{
                  serviceName
                  serviceId
                  serviceUuid
                }
              }
            }
          }
          ...UserGroupRow_userGroupsUserServiceInfo
          ...AllPermissions_userGroupsUserServiceInfo
        }
        dashboardApps(email: $encodedEmail) {
          serviceName
          tenantUuid
          ...ServiceCard_dashboardApps
        }
        userAllPermissions(email: $decodedEmail){
          tenantUuid
          permissionName
          roleCode
          serviceName
        }
      }
    `,   // no fragment with data masking for userProfile because it needs to be shared among several components
    {
      decodedEmail: user.email,
      encodedEmail: encodeURIComponent(user.email)
    }
  );
  useEffect(() => {
    if (user) {
      LogRocket.identify(user.sub, {
        name: user.name,
        email: user.email,
      });
    }
  }, [user]);

  return (
    <Switch>
      <DefaultLayout>
        <AppContext.Provider value={{userProfile, userGroupsUserServiceInfo, dashboardApps, userAllPermissions}}>
          {routes.map(route => <Route key={route.name} {...route} />)}
        </AppContext.Provider>
      </DefaultLayout>
      <Route path="/smartlabel" exact component={(x) => {
        window.location.href = x.location.pathname + "/login";  //TODO: add the /smartlabel/auth ?
        return null;
      }} />
      <Route path="/pages" exact component={(x) => {
        window.location.href = x.location.pathname;
        return null;
      }} />
    </Switch>
  );
}

export default withAuthenticationRequired(AppRoot);
